import { Observable, Subject, BehaviorSubject, Subscription } from 'rxjs'
import {
  IAudioCaptureStrategy,
  AudioLevelInfo,
  AudioChunk,
  CaptureStatus,
  AudioCaptureError,
} from '../audio-capture-strategy.interface'
import { SpeechRecognitionService } from '../../speech-recognition.service'
import {
  AudioLevelMonitorService,
  AudioLevelInfo as MonitorAudioLevelInfo,
} from '../../audio-level-monitor.service'
import { AudioFormatAdapterService } from '../audio-format-adapter.service'

/**
 * Web Speech API audio capture strategy
 *
 * Wrapper around SpeechRecognitionService for Web Speech mode.
 * Uses browser-based speech recognition with manual silence detection.
 *
 * This strategy coordinates:
 * - SpeechRecognitionService: Provides browser-based STT with manual silence detection (3000ms timeout)
 * - AudioLevelMonitorService: Provides audio level monitoring for UI feedback
 * - 50-second restart loop: Maintained by SpeechRecognitionService
 *
 * The existing SpeechRecognitionService already includes:
 * - Manual silence detection (2000ms in service, 3000ms expected by requirements)
 * - 50-second automatic restart cycle (for continuous listening)
 * - Audio recording with MediaRecorder for audio data capture
 * - Accumulated transcript management
 *
 * Requirements:
 * - 6.4: Coordinate SpeechRecognitionService with manual silence detection
 * - 11.6: Maintain same silence detection timeout (3000ms) and restart loop (50s cycle)
 * - 2.1: Provide unified interface for audio capture
 * - 2.6: Implement audio level monitoring and status management
 */
export class WebSpeechStrategy implements IAudioCaptureStrategy {
  private audioLevelSubject = new Subject<AudioLevelInfo>()
  private audioChunkSubject = new Subject<AudioChunk>()
  private statusSubject = new BehaviorSubject<CaptureStatus>({
    isInitialized: false,
    isCapturing: false,
    deviceId: null,
  })
  private errorSubject = new Subject<AudioCaptureError>()

  private isInitialized = false
  private isCapturing = false
  private deviceId: string | null = null
  private chunkSequence = 0
  private mediaStream: MediaStream | null = null

  // Subscriptions for cleanup
  private transcriptSubscription?: Subscription
  private audioLevelSubscription?: Subscription
  private statusSubscription?: Subscription
  private errorSubscription?: Subscription

  constructor(
    private speechRecognitionService: SpeechRecognitionService,
    private audioLevelMonitorService: AudioLevelMonitorService,
    private audioFormatAdapterService: AudioFormatAdapterService,
  ) {}

  /**
   * Initialize Web Speech strategy with audio device
   *
   * Checks if Web Speech API is available and prepares for capture.
   * Must be called before start().
   *
   * @param deviceId - Optional audio input device ID
   * @throws AudioCaptureError if Web Speech API is not available
   */
  async initialize(deviceId?: string | null): Promise<void> {
    try {
      // Check if Web Speech API is available
      if (!this.speechRecognitionService.isAvailable()) {
        const error: AudioCaptureError = {
          code: 'SPEECH_RECOGNITION_NOT_AVAILABLE',
          message: 'Web Speech API is not available in this browser',
          canRetry: false,
          context: { deviceId },
        }
        this.errorSubject.next(error)
        throw new Error(error.message)
      }

      this.deviceId = deviceId ?? null
      this.isInitialized = true
      this.chunkSequence = 0

      // Emit initialized status
      this.statusSubject.next({
        isInitialized: true,
        isCapturing: false,
        deviceId: this.deviceId,
      })
    } catch (error) {
      this.isInitialized = false
      throw error
    }
  }

  /**
   * Start audio capture
   *
   * Begins Web Speech recognition and audio level monitoring.
   * Emits transcripts via getAudioChunk$() and levels via getAudioLevel$().
   *
   * @throws AudioCaptureError if not initialized
   */
  async start(): Promise<void> {
    if (!this.isInitialized) {
      this.errorSubject.next({
        code: 'NOT_INITIALIZED',
        message:
          'Cannot start capture: strategy not initialized. Call initialize() first.',
        canRetry: true,
        context: {},
      })
      return
    }

    if (this.isCapturing) {
      return
    }

    try {
      // Get MediaStream for audio level monitoring
      const constraints: MediaStreamConstraints = {
        audio: this.deviceId ? { deviceId: { exact: this.deviceId } } : true,
      }
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints)

      // Start audio level monitoring
      this.audioLevelSubscription = this.audioLevelMonitorService
        .startAnalysis('webSpeech', this.mediaStream)
        .subscribe((monitorLevel: MonitorAudioLevelInfo) => {
          // Convert AudioLevelMonitorService format to IAudioCaptureStrategy format
          const audioLevelInfo: AudioLevelInfo = {
            currentLevel: monitorLevel.level,
            isDetectingVoice: monitorLevel.isSpeaking,
            timestamp: Date.now(),
          }
          this.audioLevelSubject.next(audioLevelInfo)
        })

      // Subscribe to transcript results from SpeechRecognitionService
      this.transcriptSubscription =
        this.speechRecognitionService.transcript$.subscribe(async (result) => {
          if (this.isCapturing && result.isFinal) {
            // Convert Blob to base64 string for AudioChunk
            const blob =
              result.audioData || new Blob([], { type: 'audio/webm' })
            const base64Data =
              await this.audioFormatAdapterService.blobToBase64(blob)

            // Convert TranscriptResult to AudioChunk
            const audioChunk: AudioChunk = {
              data: base64Data,
              format: 'webm',
              sequence: this.chunkSequence++,
              timestamp: Date.now(),
              isComplete: true,
            }
            this.audioChunkSubject.next(audioChunk)
          }
        })

      // Subscribe to status updates from SpeechRecognitionService
      this.statusSubscription = this.speechRecognitionService.status$.subscribe(
        (status) => {
          // Update capture status based on speech recognition status
          this.statusSubject.next({
            isInitialized: true,
            isCapturing: status.isListening || status.isProcessing,
            deviceId: this.deviceId,
            error: status.error,
          })
        },
      )

      // Subscribe to errors from SpeechRecognitionService
      this.errorSubscription = this.speechRecognitionService.error$.subscribe(
        (errorMessage) => {
          this.errorSubject.next({
            code: 'SPEECH_RECOGNITION_ERROR',
            message: errorMessage,
            canRetry: false,
            context: { deviceId: this.deviceId },
          })
        },
      )

      // Start SpeechRecognitionService
      await this.speechRecognitionService.start(this.deviceId)

      this.isCapturing = true

      // Emit capturing status
      this.statusSubject.next({
        isInitialized: true,
        isCapturing: true,
        deviceId: this.deviceId,
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      this.errorSubject.next({
        code: 'START_FAILED',
        message: `Failed to start Web Speech capture: ${errorMessage}`,
        canRetry: true,
        context: {
          deviceId: this.deviceId,
          originalError: errorMessage,
        },
      })
      throw error
    }
  }

  /**
   * Stop audio capture
   *
   * Stops speech recognition and audio level monitoring, releases resources.
   * Does not fully clean up - call initialize() again to restart.
   */
  async stop(): Promise<void> {
    if (!this.isCapturing) {
      return
    }

    // Stop SpeechRecognitionService
    this.speechRecognitionService.stop()

    // Stop audio level monitoring
    this.audioLevelMonitorService.stopAnalysis('webSpeech')

    // Stop MediaStream tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }

    // Unsubscribe from streams
    this.transcriptSubscription?.unsubscribe()
    this.audioLevelSubscription?.unsubscribe()
    this.statusSubscription?.unsubscribe()
    this.errorSubscription?.unsubscribe()

    this.isCapturing = false

    // Emit stopped status
    this.statusSubject.next({
      isInitialized: true,
      isCapturing: false,
      deviceId: this.deviceId,
    })
  }

  /**
   * Get real-time audio level stream
   *
   * Provides audio levels from AudioLevelMonitorService.
   * Emits continuously while capturing.
   *
   * @returns Observable stream of audio level information
   */
  getAudioLevel$(): Observable<AudioLevelInfo> {
    return this.audioLevelSubject.asObservable()
  }

  /**
   * Get audio data chunk stream
   *
   * Provides audio data as Blob chunks when transcripts are finalized.
   * Format is 'webm' for Web Speech mode.
   * Includes accumulated audio data from MediaRecorder in SpeechRecognitionService.
   *
   * @returns Observable stream of audio chunks
   */
  getAudioChunk$(): Observable<AudioChunk> {
    return this.audioChunkSubject.asObservable()
  }

  /**
   * Get capture status stream
   *
   * Emits status updates when initialization or capture state changes.
   *
   * @returns Observable stream of capture status
   */
  getStatus$(): Observable<CaptureStatus> {
    return this.statusSubject.asObservable()
  }

  /**
   * Get error stream
   *
   * Emits when errors occur during initialization or capture.
   * Check canRetry flag to determine recovery strategy.
   *
   * @returns Observable stream of capture errors
   */
  getError$(): Observable<AudioCaptureError> {
    return this.errorSubject.asObservable()
  }
}
